export type PartnerExportFieldKey =
  | "id"
  | "nombreCompleto"
  | "primerNombre"
  | "apellidoPaterno"
  | "apellidoMaterno"
  | "telefono"
  | "email"
  | "curp"
  | "rfc"
  | "nss"
  | "fechaNacimiento"
  | "domicilio"
  | "genero"
  | "estadoCivil"
  | "escolaridad"
  | "fechaIngreso"
  | "fechaCambioPosicion"
  | "puesto"
  | "sucursal"
  | "razonSocial"
  | "turno"
  | "salario"
  | "salarioIMSS"
  | "periodicidad"
  | "estatus"
  | "numeroReloj"
  | "medioTransporte"
  | "esLiderTurno"
  | "diasTrabajo"
  | "seguroVidaChili"
  | "beneficiarioNombre"
  | "beneficiarioTelefono"
  | "beneficiarioParentesco"
  | "beneficiarioDomicilio"
  | "tipoSangre"
  | "alergias";

export type PartnerExportStatusSlug =
  | "activo"
  | "reingreso"
  | "baja"
  | "suspendido"
  | "pendiente-baja";

export interface PartnerExportStatusOption {
  value: PartnerExportStatusSlug;
  label: string;
  description?: string;
}

export const PARTNER_EXPORT_STATUS_OPTIONS: PartnerExportStatusOption[] = [
  { value: "activo", label: "Activo" },
  { value: "baja", label: "Baja", description: "Incluye fecha y motivo de baja" },
  {
    value: "suspendido",
    label: "Suspendido",
    description: "Incluye motivo y fecha de suspensión",
  },
  {
    value: "pendiente-baja",
    label: "Pendiente por baja",
    description: "Incluye quién solicitó la baja y el motivo",
  },
];

export interface PartnerExportFieldGroup {
  title: string;
  fields: {
    key: PartnerExportFieldKey;
    label: string;
    hint?: string;
  }[];
  hint?: string;
}

export const PARTNER_EXPORT_FIELD_GROUPS: PartnerExportFieldGroup[] = [
  {
    title: "Identificación",
    fields: [
      { key: "id", label: "ID" },
      { key: "nombreCompleto", label: "Nombre completo" },
      { key: "primerNombre", label: "Primer nombre" },
      { key: "apellidoPaterno", label: "Apellido paterno" },
      { key: "apellidoMaterno", label: "Apellido materno" },
      { key: "curp", label: "CURP" },
      { key: "rfc", label: "RFC" },
      { key: "nss", label: "NSS" },
    ],
  },
  {
    title: "Contacto",
    fields: [
      { key: "telefono", label: "Teléfono" },
      { key: "email", label: "Correo electrónico" },
      { key: "domicilio", label: "Domicilio" },
    ],
  },
  {
    title: "Datos laborales",
    fields: [
      { key: "fechaIngreso", label: "Fecha de ingreso" },
      { key: "fechaCambioPosicion", label: "Fecha cambio de posición" },
      { key: "puesto", label: "Puesto" },
      { key: "sucursal", label: "Sucursal" },
      { key: "razonSocial", label: "Razón social" },
      { key: "turno", label: "Turno" },
      { key: "salario", label: "Salario" },
      { key: "salarioIMSS", label: "Salario IMSS" },
      { key: "periodicidad", label: "Periodicidad de pago" },
      { key: "numeroReloj", label: "Número de reloj" },
      { key: "esLiderTurno", label: "Líder de turno" },
      { key: "diasTrabajo", label: "Días de trabajo" },
      { key: "seguroVidaChili", label: "Seguro Chili (Sí/No)" },
      {
        key: "estatus",
        label: "Estatus en columna",
        hint: "Agrega también Motivo, Fecha y Solicitado por cuando aplique",
      },
    ],
  },
  {
    title: "Datos personales",
    fields: [
      { key: "fechaNacimiento", label: "Fecha de nacimiento" },
      { key: "genero", label: "Género" },
      { key: "estadoCivil", label: "Estado civil" },
      { key: "escolaridad", label: "Escolaridad" },
      { key: "tipoSangre", label: "Tipo de sangre" },
      { key: "alergias", label: "Alergias" },
    ],
  },
  {
    title: "Transporte",
    fields: [{ key: "medioTransporte", label: "Medio de transporte" }],
  },
  {
    title: "Beneficiario",
    fields: [
      { key: "beneficiarioNombre", label: "Nombre del beneficiario" },
      { key: "beneficiarioTelefono", label: "Teléfono del beneficiario" },
      { key: "beneficiarioParentesco", label: "Parentesco del beneficiario" },
      { key: "beneficiarioDomicilio", label: "Domicilio del beneficiario" },
    ],
  },
];

export const DEFAULT_PARTNER_EXPORT_FIELDS: PartnerExportFieldKey[] = [
  "id",
  "nombreCompleto",
  "telefono",
  "curp",
  "rfc",
  "fechaIngreso",
  "sucursal",
  "puesto",
  "razonSocial",
  "estatus",
];

export const ALL_PARTNER_EXPORT_STATUS_SLUGS: PartnerExportStatusSlug[] =
  PARTNER_EXPORT_STATUS_OPTIONS.map((option) => option.value);

export const ALL_PARTNER_EXPORT_FIELD_KEYS: PartnerExportFieldKey[] =
  PARTNER_EXPORT_FIELD_GROUPS.flatMap((group) =>
    group.fields.map((field) => field.key)
  );

export function getDefaultExportStatusesForTab(
  tab?: string
): PartnerExportStatusSlug[] {
  const normalized = (tab ?? "todos").toLowerCase();
  const map: Record<string, PartnerExportStatusSlug[]> = {
    activos: ["activo"],
    bajas: ["baja"],
    suspendidos: ["suspendido"],
    "control-bajas": ["pendiente-baja"],
    todos: ALL_PARTNER_EXPORT_STATUS_SLUGS,
  };
  return map[normalized] ?? ALL_PARTNER_EXPORT_STATUS_SLUGS;
}
